import { useAuth } from '../../../hooks'
import React, { FC, Fragment, ReactNode, useEffect, useMemo, useState } from 'react'
import { Close, Delete, Done, VisibilityOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { Button, Dialog, IconButton, Input, Select } from '../../ui'
import { useFile } from '../../../hooks/api/documentHooks'
import { useDeleteESignObject, useESignObjectsForFile } from '../../../hooks/api/eSignHooks'
import type { DocumentVersion } from '../../../lib/api/types/document.interfaces'
import PreviewModal from '../../../pages/Workspace/FilePreviewPage/PreviewModal'
import Loader from '../../common/Loader'

interface CreateESignObjectModalFormValues {
  name: string
  versionId: string
}

interface CreateESignObjectModalProps {
  isOpen: boolean
  onClose: () => void
  onCancel?: () => void
  title: string
  icon?: ReactNode
  fileId?: string | null
  initialVersionId?: string | null
  onAction: (name: string, versionId: string) => Promise<void>
  inputLabel?: ReactNode | string
  inputPlaceholder?: string
  cancelLabel?: string
  actionLabel?: string
  loading?: boolean
}

const buildVersionLabel = (version: DocumentVersion) => {
  return `Version ${version.versionNumber}${version.currentVersion ? ' (Current)' : ''}`
}

const CreateESignObjectModal: FC<CreateESignObjectModalProps> = ({
  isOpen,
  onClose,
  onCancel,
  title,
  icon,
  fileId,
  initialVersionId,
  onAction,
  inputLabel = 'Name',
  inputPlaceholder = 'Aura June Invoice',
  cancelLabel = 'Cancel',
  actionLabel = 'Create',
  loading = false,
}) => {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [isDeleteDraftConfirmationOpen, setIsDeleteDraftConfirmationOpen] =
    useState(false)

  const {
    control,
    watch,
    reset,
    formState: { isDirty },
    handleSubmit,
    setValue,
  } = useForm<CreateESignObjectModalFormValues>({
    defaultValues: {
      name: '',
      versionId: '',
    },
  })

  const { name, versionId } = watch()

  const {
    data: existingDraftsData,
    isLoading: isLoadingExistingDrafts,
    refetch: refetchExistingDrafts,
  } = useESignObjectsForFile(
    {
      fileId: fileId ?? '',
      status: 'DRAFT',
    },
    {
      enabled: isOpen && !!fileId && isAuthenticated,
      retry: false,
      staleTime: 1000 * 60,
    },
  )

  const { mutateAsync: deleteESignObject, isPending: isDeletingDraft } =
    useDeleteESignObject()

  const existingDraft = useMemo(
    () => existingDraftsData?.items?.[0] ?? null,
    [existingDraftsData?.items],
  )

  const {
    data: fileData,
    isLoading: isFileLoading,
    error: fileError,
  } = useFile(
    {
      id: fileId ?? '',
    },
    {
      enabled:
        isOpen &&
        !!fileId &&
        isAuthenticated &&
        !isLoadingExistingDrafts &&
        !existingDraft,
      staleTime: 1000 * 60 * 5,
    },
  )

  const versions = useMemo(
    () => fileData?.data?.versions ?? [],
    [fileData?.data?.versions],
  )

  const versionOptions = useMemo(
    () =>
      versions.map((version) => ({
        value: version.id,
        label: buildVersionLabel(version),
      })),
    [versions],
  )

  useEffect(() => {
    if (!isOpen) {
      setIsPreviewOpen(false)
      setIsDeleteDraftConfirmationOpen(false)
      reset({
        name: '',
        versionId: '',
      })
    }
  }, [isOpen, reset])

  useEffect(() => {
    if (!isOpen || existingDraft || !versions.length) return

    const resolvedVersionId =
      (initialVersionId &&
        versions.some((version) => version.id === initialVersionId) &&
        initialVersionId) ||
      versions.find((version) => version.currentVersion)?.id ||
      versions[versions.length - 1]?.id ||
      ''

    if (!resolvedVersionId) return

    setValue('versionId', resolvedVersionId, {
      shouldDirty: false,
      shouldTouch: false,
    })
  }, [existingDraft, initialVersionId, isOpen, setValue, versions])

  const handleClose = () => {
    onClose()
  }

  const openExistingDraft = () => {
    if (!existingDraft?.id || !existingDraft?.fileId) return

    const searchParams = new URLSearchParams()
    searchParams.set('documentId', existingDraft.fileId)
    searchParams.set('eSignObjectId', existingDraft.id)
    if (existingDraft.versionId) {
      searchParams.set('versionId', existingDraft.versionId)
    }

    handleClose()
    navigate(`/signing?${searchParams.toString()}`)
  }

  const handleDeleteDraft = async () => {
    if (!existingDraft?.id || !isAuthenticated) return

    await deleteESignObject(
      {
        eSignObjectId: existingDraft.id,
      },
      {
        onSuccess: async () => {
          setIsDeleteDraftConfirmationOpen(false)
          await refetchExistingDrafts()
        },
      },
    )
  }

  if (!isOpen) return null

  return (
    <Fragment>
      <Dialog
        isOpen={isOpen}
        onClose={handleClose}
        isDirty={isDirty}
        title={title}
        icon={icon}
        controllers={
          <div className="flex items-center justify-end w-full gap-2">
            <Button
              variant="tertiary"
              label={cancelLabel}
              onClick={onCancel ?? handleClose}
              disabled={loading || isDeletingDraft}
            />
            {existingDraft ? (
              <Button
                label="Open draft"
                onClick={openExistingDraft}
                disabled={isDeletingDraft}
              />
            ) : (
              <Button
                loading={loading}
                label={actionLabel}
                onClick={handleSubmit(async (data) => {
                  await onAction(data.name.trim(), data.versionId)
                })}
                disabled={
                  loading ||
                  isDeletingDraft ||
                  isLoadingExistingDrafts ||
                  isFileLoading ||
                  !!fileError ||
                  !name?.trim() ||
                  !versionId
                }
              />
            )}
          </div>
        }
      >
        <div className="max-w-[440px] md:w-[440px]">
          {isLoadingExistingDrafts ? (
            <div className="w-full flex items-center justify-center">
              <Loader size={25} />
            </div>
          ) : existingDraft ? (
            <div className="rounded-[10px] border border-stroke-light dark:border-stroke-light-dark px-4 py-5">
              <p className="text-base font-medium text-text-main dark:text-text-main-dark">
                A draft already exists for this file
              </p>
              <p className="mt-2 text-sm text-text-secondary dark:text-text-table-dark">
                Open the existing draft and continue editing instead of creating a new one.
              </p>
              <div className="mt-4 rounded-[10px] bg-background-light dark:bg-background-dark-light px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-main dark:text-text-main-dark">
                    {existingDraft.title || 'Untitled draft'}
                  </p>
                  <p className="mt-1 text-xs text-text-secondary dark:text-text-table-dark">
                    Status: {existingDraft.status}
                  </p>
                  {!!existingDraft.createdAt && (
                    <p className="mt-1 text-xs text-text-secondary dark:text-text-table-dark">
                      Created {new Date(existingDraft.createdAt).toLocaleString()}
                    </p>
                  )}
                </div>
                {isDeleteDraftConfirmationOpen ? (
                  <div className="flex items-center gap-2 rounded-lg border border-stroke-light dark:border-stroke-light-dark bg-white dark:bg-background-dark-light shrink-0 h-12 px-2">
                    <span className="mr-1 text-sm font-semibold text-text-main dark:text-text-main-dark">
                      Delete?
                    </span>
                    <IconButton
                      variant="secondary"
                      className="h-8 w-8"
                      icon={<Close className="fill-black dark:fill-stroke-dark" />}
                      onClick={() => setIsDeleteDraftConfirmationOpen(false)}
                      disabled={isDeletingDraft}
                    />
                    <IconButton
                      className="h-8 w-8"
                      icon={<Done className="fill-white" />}
                      onClick={handleDeleteDraft}
                      loading={isDeletingDraft}
                    />
                  </div>
                ) : (
                  <IconButton
                    variant="secondary"
                    icon={<Delete className="w-4 h-4" />}
                    tooltip="Delete draft"
                    onClick={() => setIsDeleteDraftConfirmationOpen(true)}
                    disabled={isDeletingDraft}
                    className="min-w-10 h-10 shrink-0 bg-negative dark:bg-negative ring-negative dark:ring-negative"
                  />
                )}
              </div>
            </div>
          ) : (
            <Fragment>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    label={typeof inputLabel === 'string' ? inputLabel : ''}
                    labelClassName="mb-2 text-base text-text-main"
                    value={field.value}
                    onChange={(e) => field.onChange(e)}
                    inputClassName="px-5 h-12 rounded-[10px] dark:text-text-main-dark"
                    placeholder={inputPlaceholder}
                    onEnterClick={handleSubmit(async (data) => {
                      if (!data.name?.trim() || !data.versionId) return
                      await onAction(data.name.trim(), data.versionId)
                    })}
                    wrapperClassName="mb-4"
                  />
                )}
              />

              <Controller
                name="versionId"
                control={control}
                render={({ field }) => (
                  <div className="flex items-end gap-2">
                    <Select
                      label="Version"
                      options={versionOptions}
                      value={field.value}
                      onChange={(value) => field.onChange(String(value))}
                      placeholder={isFileLoading ? 'Loading versions...' : 'Select version'}
                      disabled={loading || isFileLoading || !versionOptions?.length}
                      wrapperClassName="w-full"
                      className="h-10 w-full"
                      selectClassName='w-full'
                    />
                    <IconButton
                      variant="secondary"
                      icon={<VisibilityOutlined className="w-5 h-5" />}
                      className="min-w-10 h-10"
                      tooltip="Preview selected version"
                      onClick={() => setIsPreviewOpen(true)}
                      disabled={
                        loading ||
                        isFileLoading ||
                        !!fileError ||
                        !fileId ||
                        !isAuthenticated ||
                        !field.value
                      }
                    />
                  </div>
                )}
              />

              {fileError && (
                <p className="mt-3 text-sm text-negative">
                  Failed to load file versions.
                </p>
              )}
            </Fragment>
          )}
        </div>
      </Dialog>

      <PreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fileId={fileId ?? null}
        versionId={versionId || undefined}
        position="right"
      />
    </Fragment>
  )
}

export default CreateESignObjectModal
